import { useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { persistCustomerSession } from "./customerAccount";
import { consumeAuthNext, readBookingCart } from "../../lib/bookingCart";
import { fieldError, indianMobile10 } from "../../lib/validation";
import ForgotPassword from "../../components/auth/ForgotPassword";
import OtpActions from "../../components/auth/OtpActions";

function persistSession(data) {
  persistCustomerSession(data);
}

function routeAfterAuth(navigate, data) {
  const next = consumeAuthNext() || new URLSearchParams(window.location.search).get("next") || "";
  if (next.startsWith("/") && data.role === "customer") {
    navigate(next);
    return;
  }
  if (data.role === "customer" && readBookingCart().length) {
    navigate("/booking");
    return;
  }
  if (!data.onboarding_complete && data.role === "customer") {
    navigate("/onboarding");
  } else if (data.role === "nurse") {
    navigate("/nurse");
  } else if (data.role === "doctor") {
    navigate("/doctor");
  } else if (data.role === "ops") {
    navigate("/ops");
  } else if (data.role === "hospital") {
    navigate("/hospital-dashboard");
  } else {
    navigate("/dashboard");
  }
}

function detailMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(", ");
  return "Something went wrong";
}

export default function CustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = location.pathname.includes("signup") || location.pathname.includes("register") ? "signup" : "signin";

  const [mode, setMode] = useState(initialMode); // signin | signup
  const [method, setMethod] = useState("phone_otp");
  const role = "customer"; // staff sign in on their own portals (/nurse, /doctor, /ops)

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("form"); // form | otp
  const [otpPurpose, setOtpPurpose] = useState("login"); // login | verify
  const [hint, setHint] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const title = useMemo(
    () => (mode === "signup" ? "Create your BookMyJab account" : "Sign in to BookMyJab"),
    [mode]
  );

  const resetOtp = () => {
    setStep("form");
    setCode("");
    setHint("");
  };

  const switchMode = (next) => {
    setMode(next);
    setMethod("phone_otp");
    setError("");
    resetOtp();
    navigate(next === "signup" ? "/signup" : "/login", { replace: true });
  };

  const handleUnverified = (detail) => {
    const d = typeof detail === "object" ? detail : {};
    setEmail(d.email || email);
    setOtpPurpose("verify");
    setMethod("password");
    setStep("otp");
    setHint(d.message || "OTP sent to your email");
    setError(d.message || "Please verify your email with the OTP.");
  };

  const register = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const err = fieldError({ name: fullName, email, password, phone: phone || undefined });
      if (phone && err) { setError(err); return; }
      if (!phone) {
        const e2 = fieldError({ name: fullName, email, password });
        if (e2) { setError(e2); return; }
      }
      const { data } = await axios.post(`${config.API}/auth/customer/register`, {
        email,
        password,
        full_name: fullName,
        phone: phone ? indianMobile10(phone) : "",
      });
      setOtpPurpose("verify");
      setStep("otp");
      setHint(data.message || "OTP sent to your email");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${config.API}/auth/customer/login`, { email, password });
      persistSession(data);
      routeAfterAuth(navigate, data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.needs_verification) {
        handleUnverified(detail);
      } else {
        setError(detailMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const purpose = mode === "signup" ? "verify" : "login";
      const { data } = await axios.post(`${config.API}/auth/otp/email/send`, { email, purpose });
      setOtpPurpose(purpose);
      setStep("otp");
      setHint(data.message || "OTP sent");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const err = fieldError({ phone });
      if (err) { setError(err); return; }
      const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(phone) });
      setOtpPurpose("login");
      setStep("otp");
      setHint(data.message || "OTP sent");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let data;
      if (method === "phone_otp") {
        ({ data } = await axios.post(`${config.API}/auth/otp/verify`, {
          phone: indianMobile10(phone),
          code,
          role,
          full_name: fullName || undefined,
        }));
      } else if (otpPurpose === "verify") {
        ({ data } = await axios.post(`${config.API}/auth/customer/verify-email`, {
          email,
          code,
          purpose: "verify",
        }));
      } else {
        ({ data } = await axios.post(`${config.API}/auth/otp/email/verify`, {
          email,
          code,
          purpose: "login",
        }));
      }
      persistSession(data);
      routeAfterAuth(navigate, data);
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfcfb] flex items-center justify-center px-4 py-10" data-testid="customer-login">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#673c96]/10 p-8 shadow-sm">
        <Link
          to="/"
          className="flex flex-col items-start justify-center shrink-0 mb-6"
          data-testid="navbar-logo"
          style={{ minWidth: '170px' }}
        >
          <img
            src={config.LOGO_URL}
            alt="BookMyJab"
            style={{
              width: '160px',
              minWidth: '140px',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
          <span
            data-testid="navbar-tagline"
            className="text-[9px] md:text-[10px] italic font-medium text-[#311b52]/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Uncompromising Care. Seamlessly Yours.
          </span>
        </Link>
        <h1 className="mt-4 text-3xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{title}</h1>
        <p className="mt-2 text-sm text-[#673c96]">
          {mode === "signup"
            ? "Enter your name and mobile. We’ll send a 6-digit code."
            : "Enter your mobile number. We’ll send a 6-digit code."}
        </p>

        <div className="mt-5 flex rounded-full border border-[#673c96]/15 p-1">
          <button type="button" onClick={() => switchMode("signin")}
            className={`flex-1 rounded-full py-2 text-sm font-semibold ${mode === "signin" ? "bg-[#311b52] text-white" : "text-[#673c96]"}`}>
            Sign in
          </button>
          <button type="button" onClick={() => switchMode("signup")}
            className={`flex-1 rounded-full py-2 text-sm font-semibold ${mode === "signup" ? "bg-[#311b52] text-white" : "text-[#673c96]"}`}>
            Sign up
          </button>
        </div>

        {step === "form" && method !== "password" && (
          <form onSubmit={mode === "signup" && method !== "phone_otp" ? register : sendPhoneOtp} className="mt-6 space-y-4">
            {mode === "signup" && (
              <input required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name"
                className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="signup-name" />
            )}
            <input required value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} maxLength={10} placeholder="10-digit mobile"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="otp-phone" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="otp-send-btn">
              {loading ? "Sending…" : "Send OTP"}
            </button>
            <details className="pt-1">
              <summary className="text-xs text-[#9ca3af] cursor-pointer">Use email and password instead</summary>
              <button type="button" className="w-full text-sm text-[#673c96] mt-2"
                onClick={() => { setMethod("password"); resetOtp(); setError(""); }}>
                Continue with email
              </button>
            </details>
          </form>
        )}

        {step === "form" && method === "password" && mode === "signup" && (
          <form onSubmit={register} className="mt-6 space-y-4">
            <input required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="signup-name" />
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="signup-email" />
            <input required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (min 8 characters)" className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="signup-password" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="signup-submit">
              {loading ? "Creating…" : "Create account"}
            </button>
            <button type="button" className="w-full text-sm text-[#673c96]" onClick={() => { setMethod("phone_otp"); resetOtp(); }}>Back to mobile OTP</button>
          </form>
        )}

        {step === "form" && method === "password" && mode === "signin" && (
          <form onSubmit={passwordLogin} className="mt-6 space-y-4">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="login-email" />
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="login-password" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <button type="button" className="w-full text-sm text-[#673c96]" onClick={() => setShowForgot(true)}>Forgot password?</button>
            <button type="button" className="w-full text-sm text-[#673c96]" onClick={() => { setMethod("phone_otp"); resetOtp(); }}>Back to mobile OTP</button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp} className="mt-6 space-y-4">
            <p className="text-sm text-[#673c96]">{hint}</p>
            <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3 tracking-widest text-center" data-testid="otp-code" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="otp-verify-btn">
              {loading ? "Verifying…" : otpPurpose === "verify" ? "Verify email & continue" : "Verify & continue"}
            </button>
            <OtpActions
              loading={loading}
              changeLabel={method === "phone_otp" ? "Change number" : "Change email"}
              onChangeTarget={resetOtp}
              onResend={async () => {
                if (method === "phone_otp") {
                  const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(phone) });
                  setHint(data.message || "OTP sent");
                } else {
                  const purpose = otpPurpose === "verify" ? "verify" : "login";
                  const { data } = await axios.post(`${config.API}/auth/otp/email/send`, { email, purpose });
                  setHint(data.message || "OTP sent");
                }
              }}
            />
          </form>
        )}

        {error && <p className="mt-4 text-sm text-[#ee4036]" data-testid="auth-error">{error}</p>}
        {showForgot && <ForgotPassword onClose={() => setShowForgot(false)} />}
        <p className="mt-6 text-xs text-[#673c96]">
          By continuing you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> &{" "}
          <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
